import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import useApi from "../hooks/useApi";
import { useUser } from "../hooks/useUser";

// Specs reales con que el backend procesa cada imagen (sharp, fit: cover):
// foto de perfil 300x300 (1:1), banner 1200x400 (3:1). Solo JPG/PNG.
const SPECS = {
  profileimg: {
    label: "Foto de perfil",
    w: 300,
    h: 300,
    aspectRatio: "1 / 1",
    previewW: 160,
    redonda: true,
  },
  bannerimg: {
    label: "Banner",
    w: 1200,
    h: 400,
    aspectRatio: "3 / 1",
    previewW: 360,
    redonda: false,
  },
};

// Subida de imagen de perfil/banner con validación de formato, mención de
// dimensiones permitidas y vista previa del recorte (object-fit cover, igual
// que el backend) antes de confirmar. `endpoint` = "artista/profile" | "productor/profile".
export default function SubirImagenPerfil({
  tipo,
  endpoint,
  currentImage,
  showSnack,
}) {
  const spec = SPECS[tipo];
  const { loadApi, loadingApi } = useApi();
  const { updateUser } = useUser();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [open, setOpen] = useState(false);

  const handleSelect = ({ target }) => {
    const f = target.files?.[0];
    // permitir volver a elegir el mismo archivo después
    target.value = "";
    if (!f) return;
    if (!/\.(jpe?g|png)$/i.test(f.name)) {
      showSnack("Solo se permiten imágenes JPG o PNG.", "warning");
      return;
    }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFile(null);
    setPreviewUrl(null);
  };

  const handleConfirm = () => {
    loadApi(endpoint, true, "put", { [tipo]: file }, true)
      .then(({ data }) => {
        showSnack(data.message);
        updateUser();
        handleClose();
      })
      .catch((e) => showSnack(e.message, "error"));
  };

  const loading = loadingApi.includes(`put__${endpoint}`);

  return (
    <Stack gap={1.5}>
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
        {/* Imagen actual */}
        <Box
          sx={{
            width: spec.redonda ? 90 : 240,
            aspectRatio: spec.aspectRatio,
            borderRadius: spec.redonda ? "50%" : 2,
            overflow: "hidden",
            border: "1px solid #00000020",
            bgcolor: "#f0f1f7",
            flexShrink: 0,
          }}
        >
          {currentImage && (
            <Box
              component="img"
              src={currentImage}
              alt={spec.label}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          )}
        </Box>
        <Stack gap={0.5}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<PhotoCameraOutlinedIcon />}
            sx={{ textTransform: "none", borderRadius: 2, alignSelf: "start" }}
          >
            Cambiar {spec.label.toLowerCase()}
            <input
              type="file"
              hidden
              accept=".jpg,.jpeg,.png"
              onChange={handleSelect}
            />
          </Button>
          {/* Task 3: mención de dimensiones y formato permitidos */}
          <Typography variant="caption" color="text.secondary">
            Tamaño recomendado: <strong>{spec.w}×{spec.h}px</strong>
            {spec.redonda
              ? " (cuadrada)"
              : " (proporción 3:1, horizontal)"}. Formatos: JPG o PNG.
          </Typography>
        </Stack>
      </Stack>

      {/* Task 4: vista previa del recorte antes de confirmar */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Vista previa — {spec.label}</DialogTitle>
        <DialogContent>
          <Stack gap={2} alignItems="center">
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Tu imagen se ajustará a <strong>{spec.w}×{spec.h}px</strong>. Así
              se verá una vez guardada:
            </Typography>
            <Box
              sx={{
                width: spec.previewW,
                maxWidth: "100%",
                aspectRatio: spec.aspectRatio,
                borderRadius: spec.redonda ? "50%" : 2,
                overflow: "hidden",
                border: "2px solid #F27224",
              }}
            >
              {previewUrl && (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Vista previa recortada"
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Si la imagen no tiene esta proporción, se recortará centrada para
              encajar. Elige otra si no te convence el recorte.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button variant="text" onClick={handleClose}>
            Elegir otra
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={handleConfirm}
            sx={{
              bgcolor: "#F27224",
              "&:hover": { bgcolor: "#d9611a" },
              textTransform: "none",
            }}
          >
            Confirmar y guardar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
